import { useState, useRef } from 'react';
import { RECURRING_CONTEXTS, recurringRows, recurringSummary, recentUnmatched, mostUrgent, startsExpanded } from './recurring';
import { fmtBal, fmtCents, fmtDay, fmtDayShort } from './format';

// The Recurring tab: what is expected to go out, how often, and whether this
// cycle's charge has come. Read-only: Finance.js loads the lists and matches
// the transactions, recurring.js does the arithmetic, and this file only draws
// it. Trust is never here — the list has no Trust rows and recurring.js drops
// any that appear.

const TOGGLE = ['All', ...RECURRING_CONTEXTS];

// The year is left off a date that falls in the current one.
const fmtWhen = (d, today) => (d.slice(0, 4) === today.slice(0, 4) ? fmtDayShort(d) : fmtDay(d));

function badgeText(b, today) {
  switch (b.kind) {
    case 'paid':    return 'Paid';
    case 'due':     return 'Due';
    case 'double':  return '2× this month';
    case 'missed':  return 'Missed';
    case 'price':   return 'Price changed';
    case 'prepaid': return `Prepaid through ${fmtWhen(b.date, today)}`;
    case 'starts':  return b.date ? `Starts ${fmtWhen(b.date, today)}` : 'Not started';
    case 'ends':    return `Ends ${fmtWhen(b.date, today)}`;
    case 'ended':   return b.date ? `Ended ${fmtWhen(b.date, today)}` : 'Ended';
    default:        return b.kind;
  }
}

// One item. The same markup serves both layouts: the stylesheet lays the cells
// out as seven columns from 640px up, and as two lines on a phone (name +
// status, then the details in a row).
function Row({ row, today, showContext }) {
  const { item, last, next, avg12, badges, monthly } = row;
  const perMonthDiffers = Number.isFinite(item.ExpectedAmount) && Math.abs(monthly - item.ExpectedAmount) > 0.005;
  return (
    <div className="fin-rc-row">
      <div className="fin-rc-name">
        {item.Title}
        {showContext && <span className="fin-rc-ctx">{item.Context}</span>}
      </div>
      <div className="fin-rc-status">
        {badges.map((b, i) => (
          <span key={i} className={`fin-rc-badge fin-rc-badge--${b.kind}${b.tone ? ` fin-rc-badge--${b.tone}` : ''}`}>{badgeText(b, today)}</span>
        ))}
      </div>
      <div className="fin-rc-details">
        <div className="fin-rc-cad">{item.Cadence || '—'}</div>
        <div className="fin-rc-exp">
          {fmtCents(item.ExpectedAmount)}
          {item.IsEstimate === true && <span className="fin-rc-est">est.</span>}
          {perMonthDiffers && <span className="fin-rc-sub">{fmtBal(monthly)}/mo</span>}
        </div>
        <div className={`fin-rc-last${last ? '' : ' fin-rc-none'}`}>{last ? `${fmtWhen(last.date, today)} · ${fmtCents(last.amount)}` : '—'}</div>
        <div className={`fin-rc-next${next ? '' : ' fin-rc-none'}`}>{next ? fmtWhen(next, today) : '—'}</div>
        <div className={`fin-rc-avg${avg12 !== null ? '' : ' fin-rc-none'}`}>{avg12 !== null ? fmtCents(avg12) : '—'}</div>
      </div>
    </div>
  );
}

// A category header's summary badge: "2 due", "1 missed".
const URGENT_TEXT = { missed: 'missed', double: 'charged 2×', price: 'price changed', due: 'due' };

// The categories the user has folded or unfolded, remembered on this device:
// { category name: true (collapsed) | false (expanded) }. A category that is
// not in here has never been touched and follows the default (startsExpanded).
// Storage can be missing or refuse (private browsing, a full quota): the tab
// then shows the defaults and forgets on reload.
const FOLDS_KEY = 'dh_recurringFolds';
function readFolds() {
  try {
    const saved = JSON.parse(localStorage.getItem(FOLDS_KEY));
    if (!saved || typeof saved !== 'object' || Array.isArray(saved)) return {};
    return Object.fromEntries(Object.entries(saved).filter(([, v]) => typeof v === 'boolean'));
  } catch {
    return {};
  }
}
function writeFolds(folds) {
  try { localStorage.setItem(FOLDS_KEY, JSON.stringify(folds)); } catch { /* not remembered */ }
}

const groupId = (name) => `fin-rc-cat-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

const ColumnHeads = () => (
  <div className="fin-rc-cols" aria-hidden="true">
    <div className="fin-rc-name">Item</div>
    <div className="fin-rc-cad">Cadence</div>
    <div className="fin-rc-exp">Expected</div>
    <div className="fin-rc-last">Last charge</div>
    <div className="fin-rc-next">Next</div>
    <div className="fin-rc-status">Status</div>
    <div className="fin-rc-avg">12-mo avg</div>
  </div>
);

// categories: Finance_Categories id → { name, group } (Finance.js's map).
export default function RecurringTab({ items, match, categories, today }) {
  const [ctx, setCtx] = useState('All');
  const [folds, setFolds] = useState(readFolds);     // category name → collapsed?, once the user has chosen
  const unmatchedFold = useRef(null);
  const inCtx = (c) => ctx === 'All' || c === ctx;
  const showContext = ctx === 'All';
  const catName = (item) => categories[String(item.Category)]?.name || 'Uncategorized';

  const allRows = recurringRows(items, match, today);
  const rows    = allRows.filter(r => inCtx(r.item.Context));
  const live    = rows.filter(r => !r.ended);
  const ended   = rows.filter(r => r.ended).sort((a, b) => b.monthly - a.monthly);
  const summary = recurringSummary(rows);
  const candidates = (items || []).filter(i => i.Status === 'Candidate' && RECURRING_CONTEXTS.includes(i.Context) && inCtx(i.Context));
  const unmatched  = recentUnmatched(match.unmatched, today).filter(u => inCtx(u.context));

  // Grouped by category, biggest first; the group figure is what is being paid
  // now, so the groups add up to the "Incl. housing" line.
  const byCat = new Map();
  for (const r of live) {
    const name = catName(r.item);
    if (!byCat.has(name)) byCat.set(name, []);
    byCat.get(name).push(r);
  }
  const groups = [...byCat.entries()]
    .map(([name, list]) => ({
      name,
      rows: list.sort((a, b) => b.monthly - a.monthly || a.item.Title.localeCompare(b.item.Title)),
      monthly: list.filter(r => r.activeNow).reduce((s, r) => s + r.monthly, 0),
      top: Math.max(...list.map(r => r.monthly)),
      urgent: mostUrgent(list),
    }))
    .sort((a, b) => b.monthly - a.monthly || b.top - a.top || a.name.localeCompare(b.name));

  // A category starts collapsed unless something in it needs a look; once the
  // user has folded or unfolded it, that choice wins. Choices are kept by
  // category name, so they hold across the context toggle, and "all" means
  // every category on the list — not only the ones showing.
  const isOpen    = (g) => (g.name in folds ? !folds[g.name] : startsExpanded(g.urgent));
  const saveFolds = (next) => { setFolds(next); writeFolds(next); };
  const toggleCat = (g) => saveFolds({ ...folds, [g.name]: isOpen(g) });
  const setAll    = (collapsed) => saveFolds(Object.fromEntries(allRows.filter(r => !r.ended).map(r => [catName(r.item), collapsed])));

  // The unmatched section sits closed at the bottom, under every item — too far
  // down to work as a flag on its own. The line under the headline opens it
  // and brings it into view.
  const reviewUnmatched = () => {
    const el = unmatchedFold.current;
    if (!el) return;
    el.open = true;
    el.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };

  const pill = (active) => ({
    padding: '0.18rem 0.55rem', border: '1px solid #c4b89a', borderRadius: 4, cursor: 'pointer',
    background: active ? '#4B0082' : '#faf7ee', color: active ? '#fff' : '#5a4a3a',
    fontWeight: 600, fontSize: '0.7rem',
  });

  return (
    <div className="fin-rc">

      <div className="fin-rc-bar">
        <div className="fin-rc-toggle" role="group" aria-label="Recurring context">
          {TOGGLE.map(c => (
            <button key={c} style={pill(ctx === c)} aria-pressed={ctx === c} onClick={() => setCtx(c)}>{c}</button>
          ))}
        </div>
        <div className="fin-rc-foldall" role="group" aria-label="Categories">
          <button type="button" onClick={() => setAll(false)}>Expand all</button>
          <span aria-hidden="true"> · </span>
          <button type="button" onClick={() => setAll(true)}>Collapse all</button>
        </div>
      </div>

      {/* ── Headline ── */}
      <div className="fin-rc-top">
        <div className="fin-card fin-card--net fin-rc-head">
          <div className="fin-card-label">Recurring bills</div>
          <div className="fin-rc-value">{fmtBal(summary.bills)}<span className="fin-rc-per">/mo</span></div>
          {summary.hasHousing && (
            <div className="fin-rc-housing">
              Incl. housing {fmtBal(summary.inclHousing)}/mo
              {summary.housingEstimate && <span className="fin-rc-est">est.</span>}
            </div>
          )}
        </div>
        {unmatched.length > 0 && (
          <button type="button" className="fin-rc-flag" onClick={reviewUnmatched}>
            {unmatched.length} unmatched {unmatched.length === 1 ? 'charge' : 'charges'} from known merchants — <span className="fin-rc-flag-go">review</span>
          </button>
        )}
      </div>

      {/* ── Items by category ── */}
      {groups.length === 0 ? <p className="fin-empty">No recurring items{ctx === 'All' ? '' : ` for ${ctx}`}.</p> : (
        <>
          <ColumnHeads />
          {groups.map(g => {
            const open = isOpen(g);
            return (
              <div key={g.name} className="fin-rc-group">
                {/* The whole box is the tap target. */}
                <button type="button" className="fin-rc-group-head" aria-expanded={open} aria-controls={groupId(g.name)} onClick={() => toggleCat(g)}>
                  <span className="fin-rc-group-left">
                    <span className="fin-rc-chevron" aria-hidden="true">{open ? '▾' : '▸'}</span>
                    <span className="fin-rc-group-name">{g.name}</span>
                    <span className="fin-rc-group-count">({g.rows.length})</span>
                  </span>
                  <span className="fin-rc-group-right">
                    <span className="fin-rc-group-total">{fmtBal(g.monthly)}/mo</span>
                    {g.urgent && (
                      <span className={`fin-rc-badge fin-rc-badge--${g.urgent.kind}${g.urgent.tone ? ` fin-rc-badge--${g.urgent.tone}` : ''}`}>
                        {g.urgent.count} {URGENT_TEXT[g.urgent.kind]}
                      </span>
                    )}
                  </span>
                </button>
                <div id={groupId(g.name)} className="fin-rc-group-rows" hidden={!open}>
                  {g.rows.map(r => <Row key={r.item.id} row={r} today={today} showContext={showContext} />)}
                </div>
              </div>
            );
          })}
          <p className="fin-nw-foot">
            Status is for the current billing cycle. 12-mo avg is what actually went out per month.
          </p>
        </>
      )}

      {/* ── Collapsed sections ── */}
      {ended.length > 0 && (
        <details className="fin-rc-fold">
          <summary>Ended ({ended.length})</summary>
          {ended.map(r => <Row key={r.item.id} row={r} today={today} showContext={showContext} />)}
        </details>
      )}

      {candidates.length > 0 && (
        <details className="fin-rc-fold">
          <summary>Candidates ({candidates.length})</summary>
          <div className="fin-chart-wrap">
            <table className="fin-nw-table">
              <thead>
                <tr><th>Item</th><th>Context</th><th>Cadence</th><th className="num">Expected</th></tr>
              </thead>
              <tbody>
                {candidates.map(c => (
                  <tr key={c.id}>
                    <td>{c.Title}</td><td>{c.Context}</td><td>{c.Cadence || '—'}</td>
                    <td className="num">{fmtCents(c.ExpectedAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="fin-nw-foot">Proposed, not yet accepted — not counted above or in Runway.</p>
        </details>
      )}

      <details className="fin-rc-fold" ref={unmatchedFold}>
        <summary>Unmatched known-merchant charges ({unmatched.length})</summary>
        {unmatched.length === 0 ? <p className="fin-empty">None in the last 12 months.</p> : (
          <div className="fin-chart-wrap">
            <table className="fin-nw-table">
              <thead>
                <tr><th>Date</th><th>Merchant</th><th>Account</th><th className="num">Amount</th></tr>
              </thead>
              <tbody>
                {unmatched.map(u => (
                  <tr key={u.id}>
                    <td>{fmtDay(u.date)}</td>
                    <td>{u.merchant}</td>
                    <td>{u.mask ? `····${u.mask}` : '—'}</td>
                    <td className="num">{fmtCents(u.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="fin-nw-foot">
          Last 12 months. A charge from a merchant on this list whose amount fits none of that merchant’s items — counted as variable spend until an item is added for it.
        </p>
      </details>

    </div>
  );
}
